import React, { useMemo, useState } from 'react'
import Button from './Button'
import { cn } from './cn'

/**
 * Lightweight Tailwind replacement for CoreUI's <CDataTable>, covering the
 * subset of its API this codebase actually uses: an ordered list of
 * `fields`, optional per-field header labels, optional per-field cell
 * renderers, and simple client-side pagination.
 *
 * Unlike CDataTable's `scopedSlots` (which had each slot render its own
 * `<td>`), `renderCell` here only returns the cell's content — DataTable
 * owns the `<td>` itself, which keeps call sites from having to repeat
 * table markup.
 */
const DataTable = ({
  items,
  fields,
  headers = {},
  renderCell = {},
  pagination = false,
  itemsPerPage = 10,
  className,
}) => {
  const [page, setPage] = useState(1)
  const rows = items || []

  const pageCount = Math.max(1, Math.ceil(rows.length / itemsPerPage))
  const currentPage = Math.min(page, pageCount)

  const visibleRows = useMemo(() => {
    if (!pagination) return rows
    const start = (currentPage - 1) * itemsPerPage
    return rows.slice(start, start + itemsPerPage)
  }, [rows, pagination, currentPage, itemsPerPage])

  return (
    <div className={cn('tw-w-full', className)}>
      <div className="tw-overflow-x-auto">
        <table className="tw-w-full tw-border-collapse tw-text-sm">
          <thead>
            <tr className="tw-border-b tw-border-gray-200 tw-text-left">
              {fields.map((field) => (
                <th
                  key={field}
                  className="tw-px-2 tw-py-2 tw-font-semibold tw-text-gray-700"
                >
                  {headers[field] || field}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr
                key={item.id ?? index}
                className="tw-border-b tw-border-gray-100"
              >
                {fields.map((field) => (
                  <td key={field} className="tw-px-2 tw-py-2 tw-align-top">
                    {renderCell[field] ? renderCell[field](item) : item[field]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pagination && pageCount > 1 && (
        <div className="tw-mt-3 tw-flex tw-items-center tw-justify-end tw-gap-2">
          <Button
            color="secondary"
            disabled={currentPage <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </Button>
          <span className="tw-text-sm tw-text-gray-600">
            {currentPage} / {pageCount}
          </span>
          <Button
            color="secondary"
            disabled={currentPage >= pageCount}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          >
            Próxima
          </Button>
        </div>
      )}
    </div>
  )
}

export default DataTable
